import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DataTable,
  FormInput,
  FormSelect,
  PageHeader,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import {
  useAdminCoupons,
  useCreateCouponMutation,
  useDeleteCouponMutation,
  useUpdateCouponMutation,
} from "@/hooks/queries";
import type { CouponDto } from "@/types/coupon.dto";
import { CouponType } from "@/types/coupon.dto";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

const typeOptions = [
  { label: "Percentage", value: String(CouponType.Percentage) },
  { label: "Fixed Amount", value: String(CouponType.Fixed) },
  { label: "Free Delivery", value: String(CouponType.FreeDelivery) },
];

const toDateInput = (iso: string) => iso.slice(0, 10);

const emptyForm = {
  code: "",
  name: "",
  description: "",
  type: String(CouponType.Percentage),
  discountValue: 10,
  minOrderAmount: 0,
  maxDiscountAmount: "",
  startDate: new Date().toISOString().slice(0, 10),
  endDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  isActive: true,
  isFirstOrderOnly: false,
};

const AdminCoupons = () => {
  const { toast } = useToast();
  const { data, isLoading } = useAdminCoupons();
  const createMutation = useCreateCouponMutation();
  const updateMutation = useUpdateCouponMutation();
  const deleteMutation = useDeleteCouponMutation();
  const coupons = data?.items ?? [];
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<CouponDto | null>(null);
  const [form, setForm] = useState(emptyForm);

  const table = useDataTable<CouponDto>({
    data: coupons,
    searchKeys: ["code", "name"],
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setSheetOpen(true);
  };

  const openEdit = (coupon: CouponDto) => {
    setEditing(coupon);
    setForm({
      code: coupon.code,
      name: coupon.name,
      description: coupon.description ?? "",
      type: String(coupon.type),
      discountValue: coupon.discountValue,
      minOrderAmount: coupon.minOrderAmount,
      maxDiscountAmount: coupon.maxDiscountAmount != null ? String(coupon.maxDiscountAmount) : "",
      startDate: toDateInput(coupon.startDate),
      endDate: toDateInput(coupon.endDate),
      isActive: coupon.isActive,
      isFirstOrderOnly: coupon.isFirstOrderOnly,
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) return;
    const dto = {
      code: form.code.trim().toUpperCase(),
      name: form.name,
      description: form.description || undefined,
      type: Number(form.type) as CouponType,
      discountValue: form.discountValue,
      minOrderAmount: form.minOrderAmount,
      maxDiscountAmount: form.maxDiscountAmount ? Number(form.maxDiscountAmount) : undefined,
      startDate: new Date(form.startDate).toISOString(),
      endDate: new Date(form.endDate).toISOString(),
      isActive: form.isActive,
      isFirstOrderOnly: form.isFirstOrderOnly,
    };

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, dto });
        toast({ title: "Coupon updated" });
      } else {
        await createMutation.mutateAsync(dto);
        toast({ title: "Coupon created" });
      }
      setSheetOpen(false);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const columns: DataTableColumn<CouponDto>[] = [
    { id: "code", header: "Code", cell: (row) => <code className="font-semibold">{row.code}</code> },
    { id: "name", header: "Name", cell: (row) => row.name },
    {
      id: "discount",
      header: "Discount",
      cell: (row) =>
        row.type === CouponType.Percentage
          ? `${row.discountValue}%`
          : row.type === CouponType.Fixed
            ? `₹${row.discountValue}`
            : "Free delivery",
    },
    { id: "used", header: "Used", cell: (row) => `${row.usedCount}${row.maxUsageCount ? ` / ${row.maxUsageCount}` : ""}` },
    {
      id: "status",
      header: "Status",
      cell: (row) => (
        <Badge variant={row.isActive ? "default" : "secondary"}>
          {row.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (row) => (
        <div className="flex gap-1 justify-end">
          <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteId(row.id)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Coupons"
        description="Create and manage platform checkout coupons."
        actions={
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" /> Add Coupon
          </Button>
        }
      />

      {isLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : (
        <DataTable
          title={`All Coupons (${coupons.length})`}
          columns={columns}
          data={table.rows}
          getRowId={(row) => String(row.id)}
          search={table.state.search}
          onSearchChange={table.setSearch}
          searchPlaceholder="Search coupons…"
          sort={table.state.sort}
          onSortChange={table.toggleSort}
          pagination={table.pagination}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
        />
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl">
          <SheetHeader className="border-b px-6 py-5 text-left">
            <SheetTitle>{editing ? "Edit Coupon" : "New Coupon"}</SheetTitle>
            <SheetDescription>
              {editing ? "Update coupon rules and validity." : "Create a new checkout discount code."}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <FormInput label="Code *" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!editing} />
            <FormInput label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <FormInput label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <FormSelect label="Type" value={form.type} onValueChange={(v) => setForm({ ...form, type: v })} options={typeOptions} />
            <FormInput label="Discount Value" type="number" value={String(form.discountValue)} onChange={(e) => setForm({ ...form, discountValue: Number(e.target.value) })} />
            <FormInput label="Min Order (₹)" type="number" value={String(form.minOrderAmount)} onChange={(e) => setForm({ ...form, minOrderAmount: Number(e.target.value) })} />
            <FormInput label="Max Discount (₹)" type="number" value={form.maxDiscountAmount} onChange={(e) => setForm({ ...form, maxDiscountAmount: e.target.value })} />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormInput label="Start Date" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
              <FormInput label="End Date" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Active</Label>
              <Switch checked={form.isActive} onCheckedChange={(v) => setForm({ ...form, isActive: v })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>First order only</Label>
              <Switch checked={form.isFirstOrderOnly} onCheckedChange={(v) => setForm({ ...form, isFirstOrderOnly: v })} />
            </div>
          </div>

          <SheetFooter className="mt-auto border-t bg-background px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
            <Button variant="outline" onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>Save</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteId != null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete coupon?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteId == null) return;
                try {
                  await deleteMutation.mutateAsync(deleteId);
                  toast({ title: "Coupon deleted" });
                } catch (error) {
                  toast({ title: getApiErrorMessage(error), variant: "destructive" });
                }
                setDeleteId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCoupons;
